import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cmsSectionsApi } from "./client";

// --- Sections ---

export const cmsSectionKeys = {
  all: ["cms", "sections"] as const,
  list: (pageKey: string) => ["cms", "sections", "list", pageKey] as const,
  detail: (id: string) => ["cms", "sections", "detail", id] as const,
};

export function useCmsSectionsQuery(pageKey: string) {
  return useQuery({
    queryKey: cmsSectionKeys.list(pageKey),
    queryFn: () => cmsSectionsApi.list(pageKey),
    enabled: !!pageKey,
  });
}

export function useCmsSectionQuery(id: string) {
  return useQuery({
    queryKey: cmsSectionKeys.detail(id),
    queryFn: () => cmsSectionsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateCmsSection(pageKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: cmsSectionsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cmsSectionKeys.list(pageKey) });
      qc.invalidateQueries({ queryKey: cmsSectionKeys.all });
    },
  });
}

export function useUpdateCmsSection(id: string, pageKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof cmsSectionsApi.update>[1]) =>
      cmsSectionsApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cmsSectionKeys.detail(id) });
      qc.invalidateQueries({ queryKey: cmsSectionKeys.list(pageKey) });
      qc.invalidateQueries({ queryKey: cmsSectionKeys.all });
    },
  });
}

export function useDeleteCmsSection(pageKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cmsSectionsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cmsSectionKeys.list(pageKey) });
      qc.invalidateQueries({ queryKey: cmsSectionKeys.all });
    },
  });
}

export function useReorderCmsSections(pageKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (order: Array<{ id: string; sortOrder: number }>) =>
      cmsSectionsApi.reorder(pageKey, order),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cmsSectionKeys.list(pageKey) });
      qc.invalidateQueries({ queryKey: cmsSectionKeys.all });
    },
  });
}
